// BreadcrumbContext.tsx
import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { ISideBarBoard, ISideBarProject, ISideBarSubproject } from "../common/api_interfaces/common/ISideBar";
import { Context } from "..";
import { HighlightOptions, INavBarInfo } from "../component/LeftMenu/NavigationMenu/NavigationPanel";

export interface IBreadCrumb {
    label: string;
    url: string;
}

interface BreadCrumbContextProps {
    breadCrumb: IBreadCrumb[];
    setBreadCrumb: React.Dispatch<React.SetStateAction<IBreadCrumb[]>>;
    buildBreadCrumbs: () => void;
    findProject: (projects: ISideBarProject[], id: number, archivedPage: boolean) => IBreadCrumb[];
    findBoard: (projects: ISideBarProject[], id: number, archivedPage: boolean) => IBreadCrumb[];
    setNavBarInfo: React.Dispatch<React.SetStateAction<INavBarInfo>>;
    setArchivedPage: React.Dispatch<React.SetStateAction<boolean>>;
    isMainPage: boolean;
    sideBarProjects: ISideBarProject[];
    setSideBarProjects: React.Dispatch<React.SetStateAction<ISideBarProject[]>>;
    archivedProjects: ISideBarProject[];
    setArchivedProjects: React.Dispatch<React.SetStateAction<ISideBarProject[]>>;
    navBarInfo: INavBarInfo;
    archivedPage: boolean;
    navPanelHighlight: HighlightOptions | undefined;
    setNavPanelHighlight: React.Dispatch<React.SetStateAction<HighlightOptions | undefined>>;
    toggleProject: (id: number) => void;
    toggleBoard: (id: number) => void;
    getData: () => Promise<void>;
}

const BreadCrumbContext = createContext<BreadCrumbContextProps | undefined>(undefined);

export const useBreadCrumbContext = () => {
    const context = useContext(BreadCrumbContext);
    if (!context) {
        throw new Error("useBreadcrumb must be used within a BreadcrumbProvider");
    }
    return context;
};

interface BreadCrumbProviderProps {
    children: ReactNode;
}

const outerPages = ["/register", "/login", "/forgotPassword", "/feedback", "/recoverPassword/", "/noAccessPages"];

export const BreadCrumbProvider: React.FC<BreadCrumbProviderProps> = ({ children }) => {
    const { store } = useContext(Context);
    const [searchParams, setSearchParams] = useSearchParams();
    const [breadCrumb, setBreadCrumb] = useState<IBreadCrumb[]>([]);
    const location = useLocation();
    const [sideBarProjects, setSideBarProjects] = useState<ISideBarProject[]>([]);
    const [archivedProjects, setArchivedProjects] = useState<ISideBarProject[]>([]);
    const [navBarInfo, setNavBarInfo] = useState<INavBarInfo>({});
    const [archivedPage, setArchivedPage] = useState<boolean>(false);
    const [navPanelHighlight, setNavPanelHighlight] = useState<HighlightOptions | undefined>();
    const [lastReturnUrl, setLastReturnUrl] = useState<string>();

    const isMainPage = !outerPages.some(p => location.pathname.includes(p));

    useEffect(() => {
        if (isMainPage) {
            setLastReturnUrl(`${location.pathname}${location.search}${location.hash}`);
        } else if (!store.isAuth && lastReturnUrl) {
            setSearchParams({ returnUrl: lastReturnUrl });
        }
        buildBreadCrumbs();
    }, [store.isAuth, isMainPage, location.pathname, location.search, location.hash]);

    useEffect(() => {
        buildBreadCrumbs();
    }, [sideBarProjects]);

    useEffect(() => {
        //Этот кусок кода заполняет переменные из стора currentBoardData и currentProjectId используя информацию из breadCrumd
        if (breadCrumb && breadCrumb[breadCrumb.length - 1]?.url?.includes("board")) {
            store.setCurrentBoardData(breadCrumb[breadCrumb.length - 1]);
            const currentProjectUrl = breadCrumb[breadCrumb.length - 2].url;
            const currentProjectId: number = Number(currentProjectUrl.split("/")[2]);
            store.setCurrentProjectId(currentProjectId);
        }
        if (breadCrumb && breadCrumb[breadCrumb.length - 1]?.url?.includes("project")) {
            const currentProjectUrl = breadCrumb[breadCrumb.length - 1].url;
            const currentProjectId: number = Number(currentProjectUrl.split("/")[2]);
            store.setCurrentProjectId(currentProjectId);
        }
    }, [breadCrumb]);

    const getData = useCallback(async () => {
        await store.getProjectsForLeftMenu();

        setSideBarProjects(store.sideBar.projects.filter((p) => !p.archived));
        setArchivedProjects(store.sideBar.projects.filter((p) => p.archived));
    }, []);

    const buildBreadCrumbs = () => {
        let breadCrumbs: IBreadCrumb[] = [];
        let isArchivedPage = false;
        if (location.pathname.includes("archive")) {
            isArchivedPage = true;
            let [_, entity, id] = location.pathname.slice(1).split("/");
            breadCrumbs = [
                {
                    label: "Архив",
                    url: "/archive",
                },
            ];
            if (entity === "project") {
                breadCrumbs = [...breadCrumbs, ...findProject(archivedProjects, Number(id), true)];
            } else if (entity === "board") {
                breadCrumbs = [...breadCrumbs, ...findBoard(archivedProjects, Number(id), true)];
            }
            setBreadCrumb(breadCrumbs);
        } else if (
            (location.pathname.includes("board") && !location.pathname.includes("task")) ||
            (location.pathname.includes("project") && !location.pathname.includes("projects"))
        ) {
            setNavPanelHighlight(undefined);
            let [entity, id] = location.pathname.slice(1).split("/");
            breadCrumbs = [
                {
                    label: "Проекты",
                    url: "/projects",
                },
            ];
            if (entity === "project") {
                breadCrumbs = [...breadCrumbs, ...findProject(sideBarProjects, Number(id), false)];
            } else if (entity === "board") {
                breadCrumbs = [...breadCrumbs, ...findBoard(sideBarProjects, Number(id), false)];
            }
            setBreadCrumb(breadCrumbs);
        } else if (location.pathname.includes("projects")) {
            setNavPanelHighlight("projects");
            setNavBarInfo({ openProjectIds: [] });
            breadCrumbs = [
                {
                    label: "Проекты",
                    url: "/projects",
                },
            ];
            setBreadCrumb(breadCrumbs);
        } else if (!location.pathname.includes("task")) {
            setNavBarInfo({ openProjectIds: navBarInfo.openProjectIds });
            // setBreadCrumb(breadCrumbs);
        }
        setArchivedPage(isArchivedPage);
        if (isArchivedPage) setNavPanelHighlight("archive");
    };

    const findProject = (projects: ISideBarProject[], id: number, archivedPage: boolean): IBreadCrumb[] => {
        const breadCrumbs: IBreadCrumb[] = [];
        let project = projects.find((p) => p.id === id);
        let subproject: ISideBarSubproject | undefined;
        if (!project) {
            subproject = projects.flatMap((project) => [...(project.subprojects || [])]).find((item) => item.id === id);
            if (subproject) {
                project = projects.find((p) => p.subprojects?.includes(subproject!));
            }
        }
        if (project) {
            breadCrumbs.push({
                label: project.name,
                url: `${archivedPage ? "/archive" : ""}/project/` + project.id,
            });
        }
        if (subproject) {
            breadCrumbs.push({
                label: subproject.name,
                url: `${archivedPage ? "/archive" : ""}/project/` + subproject.id,
            });
        }
        let navBarInfoCopy = structuredClone(navBarInfo);
        navBarInfoCopy.projectId = project?.id;
        navBarInfoCopy.boardId = undefined;
        navBarInfoCopy.subprojectId = subproject?.id;
        if (project) {
            if (!navBarInfoCopy.openProjectIds) navBarInfoCopy.openProjectIds = [];
            if (!navBarInfoCopy.openProjectIds?.includes(project.id)) navBarInfoCopy.openProjectIds?.push(project.id);
        }
        if (subproject) {
            if (!navBarInfoCopy.openProjectIds) navBarInfoCopy.openProjectIds = [];
            if (!navBarInfoCopy.openProjectIds?.includes(subproject.id))
                navBarInfoCopy.openProjectIds?.push(subproject.id);
        }
        setNavBarInfo(navBarInfoCopy);
        return breadCrumbs;
    };

    const findBoard = (projects: ISideBarProject[], id: number, archivedPage: boolean): IBreadCrumb[] => {
        const breadCrumbs: IBreadCrumb[] = [];
        let board: ISideBarBoard | undefined = undefined;
        let subproject: ISideBarSubproject | undefined = undefined;
        let project: ISideBarProject | undefined = undefined;
        let parentBoard: ISideBarBoard | undefined = undefined;
        for (const p of projects) {
            if (board) break;
            for (const b of p.boards)
                if (b.id === id) {
                    board = b;
                } else if (b.boards?.find((sb) => sb.id === id)) {
                    parentBoard = b;
                    board = b.boards?.find((sb) => sb.id === id);
                }
            if (board) {
                project = p;
                break;
            } else if (p.subprojects) {
                for (const sp of p.subprojects) {
                    for (const b of sp.boards)
                        if (b.id === id) {
                            board = b;
                        } else if (b.boards?.find((sb) => sb.id === id)) {
                            parentBoard = b;
                            board = b.boards?.find((sb) => sb.id === id);
                        }
                    if (board) {
                        subproject = sp;
                        project = p;
                        break;
                    }
                }
            }
        }
        if (project)
            breadCrumbs.push({
                label: project.name,
                url: `${archivedPage ? "/archive" : ""}/project/` + project.id,
            });
        if (subproject)
            breadCrumbs.push({
                label: subproject.name,
                url: `${archivedPage ? "/archive" : ""}/project/` + subproject.id,
            });
        if (board)
            breadCrumbs.push({
                label: board.name,
                url: `${archivedPage ? "/archive" : ""}/board/` + board.id,
            });
        let navBarInfoCopy = structuredClone(navBarInfo);
        navBarInfoCopy.projectId = project?.id;
        navBarInfoCopy.subprojectId = subproject?.id;
        navBarInfoCopy.boardId = board?.id;
        if (project) {
            if (!navBarInfoCopy.openProjectIds) navBarInfoCopy.openProjectIds = [];
            if (!navBarInfoCopy.openProjectIds?.includes(project.id)) navBarInfoCopy.openProjectIds?.push(project.id);
        }
        if (subproject) {
            if (!navBarInfoCopy.openProjectIds) navBarInfoCopy.openProjectIds = [];
            if (!navBarInfoCopy.openProjectIds?.includes(subproject.id))
                navBarInfoCopy.openProjectIds?.push(subproject.id);
        }
        if (board) {
            if (!navBarInfoCopy.openBoardIds) navBarInfoCopy.openBoardIds = [];
            if (!navBarInfoCopy.openBoardIds?.includes(board.id)) navBarInfoCopy.openBoardIds?.push(board.id);
        }
        if (parentBoard) {
            if (!navBarInfoCopy.openBoardIds) navBarInfoCopy.openBoardIds = [];
            if (!navBarInfoCopy.openBoardIds?.includes(parentBoard.id))
                navBarInfoCopy.openBoardIds?.push(parentBoard.id);
        }
        setNavBarInfo(navBarInfoCopy);
        return breadCrumbs;
    };

    const toggleProject = (id: number) => {
        let navBarInfoCopy = structuredClone(navBarInfo);
        if (!navBarInfoCopy.openProjectIds) navBarInfoCopy.openProjectIds = [];
        if (navBarInfoCopy.openProjectIds?.includes(id)) {
            navBarInfoCopy.openProjectIds = navBarInfoCopy.openProjectIds.filter((i: number) => i !== id);
        } else {
            navBarInfoCopy.openProjectIds?.push(id);
        }
        setNavBarInfo(navBarInfoCopy);
    };

    // Раскрыть/Закрыть вкладку проекта в меню
    const toggleBoard = (id: number) => {
        let navBarInfoCopy = structuredClone(navBarInfo);
        if (!navBarInfoCopy.openBoardIds) navBarInfoCopy.openBoardIds = [];
        if (navBarInfoCopy.openBoardIds?.includes(id)) {
            navBarInfoCopy.openBoardIds = navBarInfoCopy.openBoardIds.filter((i: number) => i !== id);
        } else {
            navBarInfoCopy.openBoardIds?.push(id);
        }
        setNavBarInfo(navBarInfoCopy);
    };

    const value: BreadCrumbContextProps = {
        breadCrumb,
        getData,
        navPanelHighlight,
        setNavPanelHighlight,
        setBreadCrumb,
        buildBreadCrumbs,
        findProject,
        findBoard,
        setNavBarInfo,
        setArchivedPage,
        setSideBarProjects,
        setArchivedProjects,
        sideBarProjects,
        archivedProjects,
        navBarInfo,
        isMainPage,
        archivedPage,
        toggleBoard,
        toggleProject,
    };

    return <BreadCrumbContext.Provider value={value}>{children}</BreadCrumbContext.Provider>;
};
